<template>
  <div id="app">
    <Header 
      v-if="showAppHeader"
      ref="appHeader"
      @open-login="showLogin = true" 
      @open-register="showRegister = true"
    />
    <div class="app-body" :style="appBodyStyle">
      <router-view/>
      <Footer v-if="showAppFooter" />
    </div>
    <AuthDialog
      v-if="showAuthDialog"
      :show-login="showLogin" 
      :show-register="showRegister"
      @close-login="showLogin = false"
      @close-register="showRegister = false"
      @login-success="handleLoginSuccess"
      @register-success="handleRegisterSuccess"
    />
  </div>
</template>

<script>
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import AuthDialog from '@/components/AuthDialog.vue'
import { updateDeviceClasses } from '@/utils/device'

export default {
  name: 'App',
  components: { Header, Footer, AuthDialog },
  data() {
    return {
      showLogin: false,
      showRegister: false,
      headerHeight: 0
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('auth:login-required', this.handleLoginRequired)
      window.addEventListener('resize', this.handleWindowResize)
      window.addEventListener('storage', this.handleStorageChange)
      updateDeviceClasses()
    }
    this.$nextTick(this.updateHeaderHeight)
    this.syncAuthFromStorage()
    if (this.$route && this.$route.query && this.$route.query.code) {
      const isLoggedIn = this.$store.state.auth.isLoggedIn
      if (!isLoggedIn) {
        this.showRegister = false
        this.showLogin = true
      }
    }
    try {
      const flag = sessionStorage.getItem('auth:login-required')
      if (flag === '1') {
        sessionStorage.removeItem('auth:login-required')
        const token = typeof window !== 'undefined' ? localStorage.getItem('token') : ''
        if (!token) this.handleLoginRequired()
      }
    } catch (e) {
      // ignore
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('auth:login-required', this.handleLoginRequired)
      window.removeEventListener('resize', this.handleWindowResize)
      window.removeEventListener('storage', this.handleStorageChange)
    }
  },
  computed: {
    isMobileDevice() {
      if (process.env.NODE_ENV === 'development') return true
      const isUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobi|MicroMessenger|QQBrowser|UCBrowser|baidubrowser|MiuiBrowser|HuaweiBrowser|Quark/i.test(navigator.userAgent)
      const isWidth = typeof window !== 'undefined' && window.innerWidth < 768
      return isUA || isWidth
    },
    showAppHeader() {
      if (this.isMobileDevice) return false
      const name = this.$route && this.$route.name
      return name !== 'examDetail' && name !== 'practiceDetail' && name !== 'examSubmitResult'
    },
    showAppFooter() {
      return !this.isMobileDevice
    },
    showAuthDialog() {
      return !this.isMobileDevice
    },
    appBodyStyle() {
      const paddingTop = this.showAppHeader ? `${this.headerHeight}px` : '0px'
      return { paddingTop }
    }
  },
  watch: {
    '$store.state.auth.isLoggedIn'(val) {
      if (val) {
        this.showLogin = false
        this.showRegister = false
      }
    },
    showAppHeader() {
      this.$nextTick(this.updateHeaderHeight)
    },
    $route() {
      this.$nextTick(this.updateHeaderHeight)
      this.syncAuthFromStorage()
    },
    '$route.query.code'(next) {
      if (!next) return
      if (this.isMobileDevice) return
      const isLoggedIn = this.$store.state.auth.isLoggedIn
      if (isLoggedIn) return
      this.showRegister = false
      this.showLogin = true
    }
  },
  methods: {
    handleStorageChange(e) {
      const key = e && e.key
      if (key !== 'token' && key !== 'userInfo') return
      this.syncAuthFromStorage()
    },
    syncAuthFromStorage() {
      if (!this.$store || !this.$store.state || !this.$store.state.auth) return
      const token = typeof window !== 'undefined' ? localStorage.getItem('token') : ''
      const isLoggedIn = !!this.$store.state.auth.isLoggedIn

      if (!token) {
        if (isLoggedIn) this.$store.commit('auth/CLEAR_USER_INFO')
        return
      }

      if (!isLoggedIn) this.$store.commit('auth/SET_LOGIN_STATUS', true)

      const userInfoStr = typeof window !== 'undefined' ? localStorage.getItem('userInfo') : ''
      if (!userInfoStr) return
      try {
        const userInfo = JSON.parse(userInfoStr)
        this.$store.commit('auth/SET_USER_INFO', userInfo)
        const name = userInfo.realName || userInfo.nickname || userInfo.username || userInfo.phone || '用户'
        this.$store.commit('auth/SET_USERNAME', name)
      } catch (e) {
        void e
      }
    },
    updateHeaderHeight() {
      if (!this.showAppHeader) {
        this.headerHeight = 0
        return
      }
      const headerComp = this.$refs.appHeader
      const el = headerComp && headerComp.$el
      const nextHeight = el && el.offsetHeight ? el.offsetHeight : 0
      if (nextHeight !== this.headerHeight) this.headerHeight = nextHeight
    },
    handleWindowResize() {
      this.updateHeaderHeight()
      updateDeviceClasses()
    },
    handleLoginRequired() {
      if (this.$store) {
        this.$store.commit('auth/CLEAR_USER_INFO')
      }
      this.showRegister = false
      this.showLogin = true
    },
    handleLoginSuccess() {
      this.showLogin = false
      let redirect = ''
      try {
        redirect = sessionStorage.getItem('auth:login-redirect') || ''
        sessionStorage.removeItem('auth:login-required')
        sessionStorage.removeItem('auth:login-redirect')
      } catch (e) {
        // ignore
      }
      if (redirect && this.$router) {
        this.$router.push(redirect).catch(() => {})
      } else {
        window.location.reload()
      }
    },
    handleRegisterSuccess() {
      this.showRegister = false
    }
  }
}
</script>

<style lang="scss">
::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}

html, body {
  height: 100%;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  height: 100%;
  position: relative;
}

.app-body {
  min-height: 100%;
}
</style>
